/*
 * File: ContributorRouteWrapper.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 10:03 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 1, 2025 at 12:13 AM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, {
	cloneElement, useEffect, useMemo, useRef, useState
} from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

// Services
import { validateContributorAccess } from '../services/user';

// Components
import {
	ErrorComponent,
	Spinner
} from '../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES } from '../../constants/base.constants';


/**
 * Route Wrapper
 */

export const ContributorRouteWrapper = ({
	children
}) => {

	// Get url parameters
	const { aControl, bControl } = useParams();

	// Create state handlers
	const [accessStatus, setAccessStatus] = useState('idle');
	const [routerData, setRouterData] = useState({});
	const [errorCode, setErrorCode] = useState(-1);

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('contributorRouteWrapper.metaTitle', { partnerName: partner.name }),
		description: t('contributorRouteWrapper.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		shouldIndex: false
	}), [partner, t]);

	// Build error params
	const errorParams = useMemo(() => {
		if (errorCode === 404) {
			return {
				title: t('contributorRouteWrapper.error404Title'),
				description: t('contributorRouteWrapper.error404Description')
			};
		}
		if (errorCode === 403) {
			return {
				title: t('contributorRouteWrapper.error403Title'),
				description: t('contributorRouteWrapper.error403Description')
			};
		}
		if (errorCode === 400) {
			return {
				title: t('contributorRouteWrapper.error400Title'),
				description: t('contributorRouteWrapper.error400Description')
			};
		}
		return {};
	}, [errorCode, t]);

	// Create references for components
	const isMounted = useRef(true);

	// Initialize component data function
	const validateAccess = async () => {

		// Update page status
		setAccessStatus('loading');
		try {

			// Fetch data
			const { data: { result } } = await validateContributorAccess({ aControl, bControl });

			// Set data
			setRouterData(result);

			// Ensure component is mounted
			if (isMounted.current) {

				// Update page status
				setAccessStatus('success');
			}
		} catch (error) {

			// Get parameters
			const code = error?.response?.data?.error?.code || 400;

			// Ensure component is mounted
			if (isMounted.current) {

				// Update page status
				setAccessStatus('error');
				setErrorCode(code);
			}
		}
	};

	// Handle component initialization
	useEffect(() => {

		// Set state
		isMounted.current = true;

		// Validate contributor access
		validateAccess();

		// Handle actions on dismount
		return () => { isMounted.current = false; };

	}, []);

	// Render content
	if (accessStatus === 'idle' || accessStatus === 'loading') {
		return <Spinner showMeta meta={meta} />;
	}
	if (accessStatus === 'error') {
		return <ErrorComponent showMeta {...errorParams} />;
	}

	// Return wrapper children
	return cloneElement(children, { data: routerData });
};


/**
 * Configuration
 */

ContributorRouteWrapper.propTypes = {
	children: PropTypes.element
};
ContributorRouteWrapper.defaultProps = {
	children: null
};
